<template>
  <section class="sidebar-card">
    <header class="jit-header">
      <button v-if="showBack" type="button" class="jit-back" @click="$emit('back')">
        <v-icon size="18">mdi-chevron-left</v-icon><span>Markers</span>
      </button>
      <div class="jit-title-row">
        <div class="jit-title-block">
          <div class="jit-eyebrow">
            <v-icon size="13" color="#66c7b4">mdi-select-group</v-icon><span>Inspection zone</span>
          </div>
          <h3 class="jit-title">{{ props.inspectionArea ? props.inspectionArea.name : 'No zone selected' }}</h3>
        </div>
        <button v-if="props.inspectionArea && can(Permissions.InspectionZoneUpdate)" type="button" class="jit-edit"
          title="Edit zone" @click="openInspectionAreaDetailsDialog">
          <v-icon size="18">mdi-pencil-outline</v-icon>
        </button>
      </div>
    </header>

    <div v-if="props.inspectionArea" class="card-body area-content">
      <div class="isolate-row">
        <div class="isolate-text">
          <v-icon size="18" :color="isolate ? '#66c7b4' : '#8aa0aa'">mdi-select-group</v-icon>
          <span class="isolate-label">Isolate zone</span>
        </div>
        <v-switch v-model="isolate" color="#66c7b4" density="compact" hide-details inset
          @update:model-value="onIsolateToggle" />
      </div>

      <div class="area-stats">
        <div class="area-stat">
          <span class="stat-value">{{ props.inspectionArea.numberOfMarkers ?? 0 }}</span>
          <span class="stat-label">Markers</span>
        </div>
        <div class="area-stat">
          <span class="stat-value">{{ props.inspectionArea.numberOfHotspots ?? 0 }}</span>
          <span class="stat-label">Hotspots</span>
        </div>
      </div>

      <div class="lists-bar">
        <span class="lists-title">Contents</span>
        <div class="view-toggle">
          <button type="button" class="tool-btn" :class="{ active: view === 'list' }" title="List view"
            @click="view = 'list'">
            <v-icon size="18">mdi-format-list-bulleted</v-icon>
          </button>
          <button type="button" class="tool-btn" :class="{ active: view === 'grid' }" title="Grid view"
            @click="view = 'grid'">
            <v-icon size="18">mdi-view-grid-outline</v-icon>
          </button>
        </div>
      </div>

      <div class="area-lists duallo-scroll">
        <MarkerList :markers="props.inspectionArea.markers" :asset="asset" :layout="view"
          @OnMarkerSelected="locateMarker" />
        <HotSpotList :hotSpots="props.inspectionArea.hotSpotMarkers" :asset="asset" :layout="view"
          @OnHotSpotSelected="locateHotSpot" />
      </div>
    </div>

    <div v-else class="card-body area-empty">
      <v-icon size="40" color="#4a5a60">mdi-map-search-outline</v-icon>
      <h4 class="empty-title">No zone selected</h4>
      <p class="empty-subtitle">Double-click an inspection zone's edge or name label to view its metrics.</p>
    </div>
  </section>
</template>

<script setup>
import { defineEmits, defineOptions, defineProps, ref, watch } from 'vue';
import HotSpotList from './list/HotSpotList.vue';
import MarkerList from './list/MarkerList.vue';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';

const { can } = usePermissions();
const emits = defineEmits([
  'navigateToMarker',
  'navigateToHotSpot',
  'displayInspectionAreaDetails',
  'toggleIsolate',
  'back'
])

defineOptions({ name: "JITSidebar" })

const props = defineProps({
  asset: Object,
  inspectionArea: Object,
  showBack: { type: Boolean, default: false }
})

const isolate = ref(false);
const view = ref('list');   // 'list' | 'grid' — contents view for the marker + hotspot lists

// Reset the isolate toggle whenever the selected zone changes (or is cleared) so each selection
// starts un-isolated, matching the viewer (selectInspectionArea clears isolation on new selection).
watch(() => props.inspectionArea?.id, () => { isolate.value = false; });

const onIsolateToggle = (value) => { emits('toggleIsolate', !!value); }
const locateMarker = (marker) => { emits('navigateToMarker', marker); }
const locateHotSpot = (hotSpot) => { emits('navigateToHotSpot', hotSpot); }
const openInspectionAreaDetailsDialog = () => { emits('displayInspectionAreaDetails', props.inspectionArea); }
</script>

<style scoped>
.sidebar-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  /* Fill the panel box in both axes (parity with the markers panel / marker-detail panel), so as a
     direct flex child of .viewer-right it isn't sized to content and left short. */
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
}

.jit-header {
  padding: 12px 14px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.jit-back {
  display: inline-flex; align-items: center; gap: 3px;
  background: none; border: none; padding: 4px 6px 4px 2px; margin-bottom: 6px; border-radius: 8px;
  color: #cfd8dc; font: inherit; font-size: 0.92rem; font-weight: 700; cursor: pointer;
}
.jit-back:hover { color: #fff; background: rgba(255, 255, 255, 0.05); }

.jit-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.jit-title-block { min-width: 0; }

.jit-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--duallo-accent-muted);
}

.jit-title {
  font-size: 1.05rem;
  font-weight: 800;
  margin: 3px 0 0;
  color: #ffffff;
  line-height: 1.2;
  word-break: break-word;
}

.jit-edit {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--duallo-accent);
  transition: background 0.15s ease, border-color 0.15s ease;
}
.jit-edit:hover { background: rgba(100, 255, 218, 0.1); border-color: rgba(100, 255, 218, 0.35); }

.card-body {
  padding: 12px 12px;
  flex: 1 1 auto;
  min-height: 0;
}

.area-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

/* Contents bar: section title + list/grid toggle. */
.lists-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.lists-title {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
}
.view-toggle { display: inline-flex; gap: 4px; flex: 0 0 auto; }
.tool-btn {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 8px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1);
  color: #b0bec5; cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.tool-btn:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }
.tool-btn.active { background: rgba(100, 255, 218, 0.12); border-color: rgba(100, 255, 218, 0.35); color: var(--duallo-accent); }

.isolate-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 12px 4px 12px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
}

.isolate-text {
  display: flex;
  align-items: center;
  gap: 6px;
}

.isolate-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #cfd8dc;
}

.area-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.area-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 8px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
}

.stat-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--duallo-accent-muted);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.stat-label {
  font-size: 0.7rem;
  color: #8aa0aa;
  margin-top: 4px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.area-lists {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  min-height: 0;
  padding-right: 4px;
}

.area-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 16px;
  gap: 4px;
  color: #8aa0aa;
}

.empty-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #cfd8dc;
  margin: 10px 0 2px;
}

.empty-subtitle {
  font-size: 0.82rem;
  color: #8aa0aa;
  margin: 0;
  line-height: 1.4;
}
</style>
